import { refContainsTarget } from "@prismicio/editor-support/React"
import { useColorField } from "@react-aria/color"
import {
  type Color,
  type ColorFieldProps,
  parseColor,
  useColorFieldState,
} from "@react-stately/color"
import clsx from "clsx"
import { useRef, useState } from "react"

import { IconButton } from "../IconButton"
import { Popover, type PopoverInteractOutsideEvent } from "../Popover"
import { Text } from "../Text"
import { ColorArea } from "./ColorArea"
import styles from "./ColorInput.module.css"
import { ColorSlider } from "./ColorSlider"

export interface ColorInputProps {
  value?: string
  placeholder?: string
  "aria-labelledby": string
  onValueChange: (value?: string) => void
}

export function ColorInput(props: ColorInputProps) {
  const { onValueChange, ...otherProps } = props

  const value =
    props.value?.length === 7 ? props.value.toUpperCase() : undefined

  const inputProps = {
    ...otherProps,
    value,
    onChange: (newColor: Color | string | null) => {
      const isIncompleteColor = typeof newColor === "string" // The color is not valid yet.
      if (isIncompleteColor) return
      onValueChange(newColor?.toString("hex"))
    },
  }

  const [hsbColor, setHsbColor] = useHSBColor(value)

  const colorPickerProps = {
    ...otherProps,
    value: hsbColor ?? hexToHsb("#7EDE43"),
    onChange: (newColor: Color | null) => {
      setHsbColor(newColor ?? undefined)
      onValueChange(newColor?.toString("hex"))
    },
  }

  const [popoverOpen, setPopoverOpen] = useState(false)

  const buttonRef = useRef<HTMLButtonElement>(null)

  function onButtonClick() {
    setPopoverOpen((current) => !current)
  }

  function onEscapeKeyDown() {
    setPopoverOpen(false)
  }

  function onInteractOutside(event: PopoverInteractOutsideEvent) {
    if (refContainsTarget(buttonRef, event.target)) return
    setPopoverOpen(false)
  }

  return (
    <Popover
      align="start"
      sideOffset={2}
      open={popoverOpen}
      onEscapeKeyDown={onEscapeKeyDown}
      onInteractOutside={onInteractOutside}
      anchor={
        <div className={styles.container}>
          {/* The key makes sure that the input resets its state when we don't have a value.
              This is because the library does not update its internal state as we expect in this scenario. */}
          <Input {...inputProps} key={Boolean(value).toString()} />
          <button
            className={clsx(
              styles.colorSquare,
              !value && styles.emptyColorSquare,
            )}
            ref={buttonRef}
            onClick={onButtonClick}
            style={{ backgroundColor: value }}
            aria-label={value}
          />
        </div>
      }
    >
      <div className={styles.areaContainer}>
        <div className={styles.header}>
          <Text variant="smallBold">Choose a color</Text>
          <IconButton icon="close" size="small" onClick={onButtonClick} />
        </div>
        <ColorArea {...colorPickerProps} />
        <div className={styles.footer}>
          <ColorSlider {...colorPickerProps} />
        </div>
      </div>
    </Popover>
  )
}

// Keeps a local copy of the HSB color to avoid parsing the wrong one.
function useHSBColor(value?: string) {
  const [color, setColor] = useState<Color | undefined>(() => hexToHsb(value))

  // If the formatted version doesn't match anymore, parse it again.
  if (value !== color?.toString("hex")) {
    setColor(hexToHsb(value))
  }

  return [color, setColor] as const
}

function hexToHsb(value?: string) {
  try {
    return parseColor(value ?? "").toFormat("hsb")
  } catch (_) {
    return undefined
  }
}

function Input(props: ColorFieldProps) {
  const state = useColorFieldState(props)
  const inputRef = useRef(null)
  const { inputProps } = useColorField(props, state, inputRef)

  return <input {...inputProps} className={styles.input} ref={inputRef} />
}
